import { FormEvent, useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PRODUCT_NAME, PUBLIC_VENDOR_RFQ_URL } from '../constants';
import { materialInventoryNeed, mapMaterialItemFromApi } from '../domain/procurementRequest';
import type { MaterialItem } from '../domain/procurementRequest';

/** Shape from GET /api/public/rfq/{token} (camelCase; PascalCase tolerated). */
interface VendorQuoteFormDto {
  materialsSnapshot: Record<string, unknown>[];
  incotermsSnapshot: string;
  requirements: string;
  rfqNumber: string;
  requestNumber: string;
  vendorName: string;
  materialItemIds: string[];
  /** When omitted, treat as true — this endpoint only serves tubular/string-design RFQs. */
  useTubularStringQuoteForm: boolean;
}

function readTubularFormFlag(raw: Record<string, unknown>): boolean {
  const camel = raw.useTubularStringQuoteForm;
  const pascal = raw.UseTubularStringQuoteForm;
  if (typeof camel === 'boolean') {
    return camel;
  }
  if (typeof pascal === 'boolean') {
    return pascal;
  }
  return true;
}

interface TubularLineFormState {
  mill: string;
  countryOfOrigin: string;
  requiredQtyFt: string;
  pricePerFt: string;
  truckloadCount: string;
  costPerTruckload: string;
}

function emptyLineState(defaultQty: number): TubularLineFormState {
  return {
    mill: '',
    countryOfOrigin: '',
    requiredQtyFt: defaultQty > 0 ? String(defaultQty) : '',
    pricePerFt: '',
    truckloadCount: '',
    costPerTruckload: ''
  };
}

function materialLabel(m: MaterialItem): string {
  const name = m.name?.trim() ?? '';
  const desc = m.description?.trim() ?? '';
  if (name && desc) {
    return `${name} — ${desc}`;
  }
  return name || desc || 'Material line';
}

export function VendorQuotePage(): ReactElement {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') ?? '').trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<VendorQuoteFormDto | null>(null);
  const [lineForms, setLineForms] = useState<Record<string, TubularLineFormState>>({});
  const [incoterms, setIncoterms] = useState('');
  const [freightAmount, setFreightAmount] = useState('0');
  const [miscAmount, setMiscAmount] = useState('0');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setError('This page needs a valid link from your email (missing token).');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${PUBLIC_VENDOR_RFQ_URL}/${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: { Accept: 'application/json' }
      });
      const text = await response.text();
      if (!response.ok) {
        setError(text || `Could not load quote form (${response.status}).`);
        setForm(null);
        return;
      }
      const raw = JSON.parse(text) as Record<string, unknown>;
      const useTubularStringQuoteForm = readTubularFormFlag(raw);
      const data: VendorQuoteFormDto = {
        materialsSnapshot: (raw.materialsSnapshot as Record<string, unknown>[]) ?? [],
        incotermsSnapshot: String(raw.incotermsSnapshot ?? ''),
        requirements: String(raw.requirements ?? ''),
        rfqNumber: String(raw.rfqNumber ?? ''),
        requestNumber: String(raw.requestNumber ?? ''),
        vendorName: String(raw.vendorName ?? ''),
        materialItemIds: Array.isArray(raw.materialItemIds)
          ? (raw.materialItemIds as unknown[]).map((x) => String(x ?? ''))
          : [],
        useTubularStringQuoteForm
      };
      setForm(data);
      setIncoterms(data.incotermsSnapshot ?? '');

      const byId: Record<string, TubularLineFormState> = {};
      const snapshotById = new Map<string, MaterialItem>();
      for (const raw of data.materialsSnapshot ?? []) {
        try {
          const m = mapMaterialItemFromApi(raw);
          if (m.itemId?.trim()) {
            snapshotById.set(m.itemId.trim(), m);
          }
        } catch {
          /* ignore bad snapshot rows */
        }
      }
      for (const id of data.materialItemIds ?? []) {
        const trimmed = id?.trim();
        if (!trimmed) {
          continue;
        }
        const m = snapshotById.get(trimmed);
        const defQty = m ? materialInventoryNeed(m).remaining : 0;
        byId[trimmed] = emptyLineState(defQty);
      }
      setLineForms(byId);
    } catch {
      setError('Could not load the quote form. Check your connection and try again.');
      setForm(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const materialsById = useMemo(() => {
    const map = new Map<string, MaterialItem>();
    for (const raw of form?.materialsSnapshot ?? []) {
      try {
        const m = mapMaterialItemFromApi(raw);
        if (m.itemId?.trim()) {
          map.set(m.itemId.trim(), m);
        }
      } catch {
        /* skip */
      }
    }
    return map;
  }, [form?.materialsSnapshot]);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!form || !token) {
      return;
    }

    if (!form.useTubularStringQuoteForm) {
      toast.error('This quote form is no longer supported. Contact the buyer.');
      return;
    }

    const lineQuotes: {
      itemId: string;
      mill: string;
      countryOfOrigin: string;
      requiredQtyFt: number;
      pricePerFt: number;
      truckloadCount: number;
      costPerTruckload: number;
    }[] = [];

    for (const itemId of form.materialItemIds) {
      const id = itemId?.trim();
      if (!id) {
        continue;
      }
      const f = lineForms[id];
      if (!f) {
        toast.error(`Missing quote fields for line ${id}.`);
        return;
      }
      const mill = f.mill.trim();
      const country = f.countryOfOrigin.trim();
      const lineLabel = materialsById.get(id) ? materialLabel(materialsById.get(id)!) : `line ${id}`;
      if (!mill) {
        toast.error(`Mill is required for ${lineLabel}.`);
        return;
      }
      if (!country) {
        toast.error(`Country of origin is required for ${lineLabel}.`);
        return;
      }
      const reqFt = Number(f.requiredQtyFt);
      const ppf = Number(f.pricePerFt);
      const trucks = Number(f.truckloadCount);
      const cpt = Number(f.costPerTruckload);
      if (Number.isNaN(reqFt) || reqFt <= 0) {
        toast.error(`Required quantity (ft.) must be a positive number for line ${id}.`);
        return;
      }
      if (Number.isNaN(ppf) || ppf < 0) {
        toast.error(`Price / ft. must be a valid non‑negative number for line ${id}.`);
        return;
      }
      if (Number.isNaN(trucks) || trucks < 0 || Number.isNaN(cpt) || cpt < 0) {
        toast.error(`Truckloads and cost per truckload must be valid non‑negative numbers for line ${id}.`);
        return;
      }
      lineQuotes.push({
        itemId: id,
        mill,
        countryOfOrigin: country,
        requiredQtyFt: reqFt,
        pricePerFt: ppf,
        truckloadCount: trucks,
        costPerTruckload: cpt
      });
    }

    if (lineQuotes.length !== form.materialItemIds.filter((x) => x?.trim()).length) {
      toast.error('Provide a quote for every material line.');
      return;
    }

    if (!incoterms.trim()) {
      toast.error('Incoterms are required.');
      return;
    }

    const freight = Number(freightAmount);
    const misc = Number(miscAmount);
    if (Number.isNaN(freight) || Number.isNaN(misc) || freight < 0 || misc < 0) {
      toast.error('Freight and miscellaneous fee must be valid non‑negative numbers.');
      return;
    }

    const payload = {
      lineQuotes,
      incoterms: incoterms.trim(),
      freightAmount: freight,
      miscellaneousFeeAmount: misc,
      attachmentFileName: file ? file.name : null
    };

    const body = new FormData();
    body.append('payload', JSON.stringify(payload));
    if (file) {
      body.append('file', file);
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${PUBLIC_VENDOR_RFQ_URL}/${encodeURIComponent(token)}/submit`, {
        method: 'POST',
        body
      });
      const text = await response.text();
      if (!response.ok) {
        toast.error(text || `Submit failed (${response.status}).`);
        return;
      }
      setDone(true);
      toast.success('Quote submitted. Thank you.');
    } catch {
      toast.error('Could not submit. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function updateLine(itemId: string, patch: Partial<TubularLineFormState>): void {
    setLineForms((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], ...patch }
    }));
  }

  return (
    <div className="vendor-quote-page">
      <header className="vendor-quote-header">
        <h1>{PRODUCT_NAME}</h1>
        <p className="hint">Vendor quote request — no login required</p>
      </header>

      {loading && <p className="hint">Loading quote request…</p>}

      {!loading && error && (
        <div className="card vendor-quote-card">
          <p className="error">{error}</p>
          <p className="hint">If your link has expired or was already used, contact the buyer who sent the invitation.</p>
        </div>
      )}

      {!loading && !error && done && form && (
        <div className="card vendor-quote-card">
          <h2>Quote submitted</h2>
          <p>
            Thank you{form.vendorName ? `, ${form.vendorName}` : ''}. Your quote for{' '}
            <strong>{form.rfqNumber}</strong> (request <strong>{form.requestNumber}</strong>) has been received.
          </p>
          <p className="hint">You can close this window.</p>
        </div>
      )}

      {!loading && !error && !done && form && (
        <form className="card vendor-quote-card procurement-request-form" onSubmit={(e) => void onSubmit(e)}>
          <h2>
            Quote for {form.rfqNumber}{' '}
            <span className="hint">
              ({form.requestNumber}) — {form.vendorName}
            </span>
          </h2>

          {form.requirements.trim() ? (
            <section className="vendor-quote-block">
              <h3 className="po-subheading">Buyer instructions</h3>
              <p className="multiline">{form.requirements}</p>
            </section>
          ) : null}

          <section className="vendor-quote-block">
            <h3 className="po-subheading">Material lines — tubular / string design</h3>
            <p className="hint">
              Enter mill, origin, footage, price per foot, and trucking. Totals are calculated when your quote is
              received.
            </p>
            <div className="vendor-quote-lines vendor-quote-tubular-lines">
              {form.materialItemIds.map((id) => {
                const itemId = id?.trim();
                if (!itemId) {
                  return null;
                }
                const m = materialsById.get(itemId);
                const f = lineForms[itemId] ?? emptyLineState(m ? materialInventoryNeed(m).remaining : 0);
                return (
                  <fieldset key={itemId} className="vendor-quote-tubular-fieldset">
                    <legend className="form-section-title">{materialLabel(m ?? ({} as MaterialItem))}</legend>
                    <div className="grid vendor-quote-tubular-grid">
                      <label className="full-width">
                        Mill
                        <input
                          value={f.mill}
                          onChange={(e) => updateLine(itemId, { mill: e.target.value })}
                          placeholder="e.g. TOP Seamless"
                          required
                        />
                      </label>
                      <label className="full-width">
                        Country of origin
                        <input
                          value={f.countryOfOrigin}
                          onChange={(e) => updateLine(itemId, { countryOfOrigin: e.target.value })}
                          placeholder="e.g. Thailand"
                          required
                        />
                      </label>
                      <label className="full-width">
                        Required qty (ft.)
                        <input
                          type="text"
                          inputMode="decimal"
                          value={f.requiredQtyFt}
                          onChange={(e) => updateLine(itemId, { requiredQtyFt: e.target.value })}
                          required
                        />
                      </label>
                      <label className="full-width">
                        Price / ft.
                        <input
                          type="text"
                          inputMode="decimal"
                          value={f.pricePerFt}
                          onChange={(e) => updateLine(itemId, { pricePerFt: e.target.value })}
                          required
                        />
                      </label>
                      <label className="full-width">
                        No. of truckloads
                        <input
                          type="text"
                          inputMode="decimal"
                          value={f.truckloadCount}
                          onChange={(e) => updateLine(itemId, { truckloadCount: e.target.value })}
                          placeholder="0"
                          required
                        />
                      </label>
                      <label className="full-width">
                        Cost of 1 truckload
                        <input
                          type="text"
                          inputMode="decimal"
                          value={f.costPerTruckload}
                          onChange={(e) => updateLine(itemId, { costPerTruckload: e.target.value })}
                          placeholder="0"
                          required
                        />
                      </label>
                    </div>
                  </fieldset>
                );
              })}
            </div>
          </section>

          <label className="full-width">
            Incoterms
            <input
              value={incoterms}
              onChange={(e) => setIncoterms(e.target.value)}
              required
              placeholder="e.g. FOB, DDP"
            />
          </label>

          <div className="grid vendor-quote-fees">
            <label className="full-width">
              Freight
              <input
                type="text"
                inputMode="decimal"
                value={freightAmount}
                onChange={(e) => setFreightAmount(e.target.value)}
              />
            </label>
            <label className="full-width">
              Miscellaneous fee
              <input
                type="text"
                inputMode="decimal"
                value={miscAmount}
                onChange={(e) => setMiscAmount(e.target.value)}
              />
            </label>
          </div>

          <label className="full-width">
            Attachment (optional, PDF only)
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <span className="hint">If uploads are not configured on the server, leave this empty or contact the buyer.</span>
          </label>

          <div className="row">
            <button type="submit" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit quote'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
